import qs from "qs"
import { PopoverClose } from "@radix-ui/react-popover"
import { useCallback, useEffect, useState, Fragment } from "react"
import { LoaderCircleIcon, PencilIcon, PlusIcon, TrashIcon } from "lucide-react"
import { Outlet, useFetcher, useLoaderData, useRouteLoaderData } from "react-router"

import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Dialog, DialogClose, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { ThemeUsageLabel, UsageOptions } from "@/components/ThemeUsage"
import { FiltersWrapper, type OnBeforeSubmit, type StoredSession } from "@/components/Filters"

import { hasPermission } from "@/machines/Authmachine"
import { getOrganisations } from "@/utils/projects"
import { fetchAPI, handleResponse, throwIfNotOk } from "@/utils"

import type { RootLoader } from "@/routes/root"
import type { EvidenceTheme } from "@/utils/types"
import { FieldInfoComponent, type FieldDetail } from "@/components/FieldInfo"
import type { Project } from "@/utils/types"

const routeId = "evidence-themes"
const sessionKey = "evidence-themes-filter-storage"

const BRAND_EVIDENCE_FIELDS = {
  NAME: "name",
  CODE: "code",
  USAGE: "usage",
  ORDER: "order",
} as const

// Type definition for loader data
type LoaderData = {
  evidenceCategories: EvidenceTheme[]
  loadedOrganisation: string
  projects: string[]
  loadedProject: string
  fieldInfoArray: FieldDetail[]
}

type ActionData = {
  action: string
  errorMessage?: string
  key?: string
}

type MainActionData = {
  action: string
  errorMessage?: string
}

async function loader(): Promise<LoaderData> {
  const organisations = getOrganisations()
  if (organisations.length < 1) throw new Error("No organisations found")

  const filters = qs.parse(sessionStorage.getItem(sessionKey) ?? "") as unknown as Partial<StoredSession>

  const loadedOrganisation = filters.parsed?.organisation ?? organisations[0]

  const fieldInfoArray = await fetchAPI<FieldDetail[]>(`/api/sections/Unity Viewer/brand evidence themes`)
    .then(handleResponse)
    .catch(() => [] as FieldDetail[])

  const projects = await fetchAPI<Project[]>(`/api/my/projects/${loadedOrganisation}`)
    .then(handleResponse)
    .then((projects) => projects.map((prj) => prj.name))
    .catch(() => [] as string[])

  if (projects.length === 0) {
    return { evidenceCategories: [], loadedOrganisation, projects: [], loadedProject: "", fieldInfoArray }
  }

  const loadedProject =
    filters.parsed?.project == null
      ? projects[0]
      : (projects.find((prj) => prj === filters.parsed?.project) ?? projects[0])

  const evidenceCategories = await fetchAPI<EvidenceTheme[]>(
    `/api/UnityViewer/EvidenceTheme/${loadedOrganisation}/${loadedProject}`,
  )
    .then(handleResponse)
    .catch(() => [] as EvidenceTheme[])

  return { evidenceCategories, loadedOrganisation, projects, loadedProject, fieldInfoArray }
}

async function action({ request }: { request: Request }): Promise<ActionData> {
  const formData = await request.formData()
  const { action, organisation, project } = Object.fromEntries(formData) as Record<string, string>

  let errorMessage: string | undefined

  if (organisation == null) throw new Error("Missing organisation")
  if (project == null) throw new Error("Missing project")

  switch (action) {
    case "CREATE": {
      const name = ((formData.get("name") ?? "") as string).trim()
      const code = ((formData.get("code") ?? "") as string).trim()
      const order = +((formData.get("order") ?? "") as string).trim()
      const usages = formData.getAll("usage") as string[]

      if (name === "") errorMessage = "Theme Name is required"
      else if (code === "") errorMessage = "Theme Code is required"
      else if (isNaN(order)) errorMessage = "Theme Order is required"
      else if (usages.length === 0) errorMessage = "Usage is required"

      if (errorMessage != null) break

      try {
        await fetchAPI(`/api/UnityViewer/EvidenceTheme/${organisation}/${project}`, {
          method: "POST",
          body: { name, code, usages, order },
        }).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        errorMessage =
          error instanceof Response && error.status === 304
            ? "A theme with this name already exists for the selected usage."
            : "Error creating theme"
      }

      return { action, errorMessage }
    }
    case "DELETE_ALL": {
      try {
        await fetchAPI(`/api/UnityViewer/EvidenceTheme/${organisation}/${project}`, { method: "DELETE" }).then(
          throwIfNotOk,
        )
        return { action, errorMessage, key: Math.random().toString(36) }
      } catch (error) {
        console.error(error)
        errorMessage = "Error deleting all themes"
      }
      break
    }
    default:
      throw new Error("Unknown action")
  }

  return { action, errorMessage }
}

async function mainAction({ request }: { request: Request }): Promise<MainActionData> {
  const formData = await request.formData()
  const { action, organisation, project, name, usage, code, order } = Object.fromEntries(formData) as Record<
    string,
    string
  >

  if (organisation == null) throw new Error("Missing organisation")
  if (project == null) throw new Error("Missing project")

  switch (action) {
    case "DELETE": {
      try {
        await fetchAPI(`/api/UnityViewer/EvidenceTheme/${organisation}/${project}/${usage}/${name}`, {
          method: "DELETE",
        }).then(throwIfNotOk)
      } catch (error) {
        console.error(error)
        return { action, errorMessage: "Couldn't delete theme usage" }
      }
      break
    }
    case "EDIT": {
      {
        const newOrder = +(formData.get("newOrder") as string)
        const newUsage = (formData.get("newUsage") as string).trim()

        const usageUpdate = {
          newOrder: newOrder === +order ? undefined : newOrder,
          newUsage: newUsage === usage.trim() ? undefined : newUsage,
        }

        if (usageUpdate.newOrder != null || usageUpdate.newUsage != null) {
          try {
            await fetchAPI(`/api/UnityViewer/EvidenceTheme/${organisation}/${project}/${usage}/${name}`, {
              method: "PATCH",
              body: usageUpdate,
            }).then(throwIfNotOk)
          } catch (error) {
            console.error(error)
            if (error instanceof Response && error.status === 304) {
              return {
                action,
                errorMessage: "That theme already exists with this usage. Please choose a different one.",
              }
            }
            return { action, errorMessage: "Error updating Usage/Usage Order" }
          }
        }
      }

      {
        const newCode = (formData.get("newCode") as string).trim()
        const newName = (formData.get("newName") as string).trim()

        const themeUpdate = {
          newCode: newCode === code.trim() ? undefined : newCode,
          newName: newName === name.trim() ? undefined : newName,
        }

        if (themeUpdate.newCode != null || themeUpdate.newName != null) {
          try {
            await fetchAPI(`/api/UnityViewer/EvidenceTheme/rename/${organisation}/${name}`, {
              method: "PATCH",
              body: themeUpdate,
            }).then(throwIfNotOk)
          } catch (error) {
            console.error(error)
            if (error instanceof Response && error.status === 304) {
              return {
                action,
                errorMessage: "A theme with this name already exists in this organisation. Please use a different name.",
              }
            }
            return { action, errorMessage: "Error updating Theme Name/Theme Code" }
          }
        }
      }
      break
    }

    default:
      throw new Error("Unknown action")
  }

  return { action }
}

export function BrandEvidenceThemes() {
  const rootData = useRouteLoaderData<RootLoader>("root")
  const data = useRouteLoaderData<LoaderData>(routeId)

  const onBeforeSubmit = useCallback<OnBeforeSubmit>((data, reason) => {
    if (reason.type === "field" && reason.name === "organisation") data.delete("project")
  }, [])

  if (rootData == null || data == null) return null

  return (
    <Fragment>
      <div className="flex items-center gap-4">
        {rootData.organisations.length > 1 && (
          <FiltersWrapper submitOnChange canReset={false} sessionKey={sessionKey} onBeforeSubmit={onBeforeSubmit}>
            <FiltersWrapper.Select
              name="organisation"
              label="Organisation"
              defaultValue={data.loadedOrganisation}
              items={rootData.organisations}
            />
            {data.projects.length > 1 && (
              <FiltersWrapper.Select
                name="project"
                label="Project"
                defaultValue={data.loadedProject}
                items={data.projects}
              />
            )}
          </FiltersWrapper>
        )}
        <div className="flex-1" />
        <BrandEvidenceThemesAddNew />
        <BrandEvidenceThemesDeleteAll />
      </div>
      <Outlet />
    </Fragment>
  )
}

function Main() {
  const data = useLoaderData<LoaderData>()

  return (
    <div className="h-full overflow-y-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              <span className="sr-only">No.</span>
            </TableHead>
            <TableHead className="text-left">Name</TableHead>
            <TableHead className="text-left">Code</TableHead>
            <TableHead className="text-left">Usage</TableHead>
            <TableHead>
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.evidenceCategories.length > 0 ? (
            data.evidenceCategories.map((element, index) => (
              <TableRow key={`${element.name}-${element.usage}`} className="text-sm">
                <TableCell>{index + 1}.</TableCell>
                <TableCell>{element.name}</TableCell>
                <TableCell>{element.code}</TableCell>
                <TableCell>
                  <ThemeUsageLabel usage={element.usage} />
                </TableCell>
                <TableCell className="space-x-2">
                  <BrandEvidenceThemeEdit {...element} />
                  <BrandEvidenceThemeDelete {...element} />
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={5} className="py-4 text-center text-gray-500">
                No data found for this organisation.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Outlet />
    </div>
  )
}

function BrandEvidenceThemesAddNew() {
  const data = useRouteLoaderData<LoaderData>(routeId)
  const [open, setOpen] = useState(false)

  const closeDialog = useCallback(() => setOpen(false), [])

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <PlusIcon /> Add New
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Evidence Theme</DialogTitle>
        </DialogHeader>
        <BrandEvidenceThemesAddNewForm closeDialog={closeDialog} />
      </DialogContent>
    </Dialog>
  )
}

function BrandEvidenceThemesAddNewForm({ closeDialog }: { closeDialog: () => void }) {
  const fetcher = useFetcher<ActionData>()
  const data = useRouteLoaderData<LoaderData>(routeId)

  const [formKey, setFormKey] = useState<string>()
  const [order, setOrder] = useState<number>()
  const [createAnother, setCreateAnother] = useState(false)

  useEffect(() => {
    if (fetcher.state !== "loading" || fetcher.data?.action !== "CREATE" || fetcher.data?.errorMessage != null) return

    setOrder((order) => (order ?? 1) + 1)

    if (createAnother) setFormKey(Math.random().toString(36))
    else closeDialog()
  }, [closeDialog, createAnother, fetcher.data?.action, fetcher.data?.errorMessage, fetcher.state])

  if (data == null) return null

  return (
    <fetcher.Form method="POST" className="space-y-4">
      <input type="hidden" name="action" value="CREATE" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />

      <div>
        <div className="mb-2 flex items-center gap-2">
          <label className="text-sm font-medium">Theme Name</label>
          <FieldInfoComponent name={BRAND_EVIDENCE_FIELDS.NAME} fieldDetails={data?.fieldInfoArray ?? []} />
        </div>
        <Input key={formKey} required type="text" name="name" placeholder="Enter theme name" autoFocus />
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2">
          <label className="text-sm font-medium">Code</label>
          <FieldInfoComponent name={BRAND_EVIDENCE_FIELDS.CODE} fieldDetails={data?.fieldInfoArray ?? []} />
        </div>
        <Input key={formKey} required type="text" name="code" placeholder="Enter theme code" />
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2">
          <label htmlFor="theme-usage" className="text-sm font-medium">
            Usage
          </label>
          <FieldInfoComponent name={BRAND_EVIDENCE_FIELDS.USAGE} fieldDetails={data?.fieldInfoArray ?? []} />
        </div>
        <select
          multiple
          required
          name="usage"
          id="theme-usage"
          className="w-full rounded-md border p-2"
          size={UsageOptions.length}
        >
          {UsageOptions.map((option) => (
            <option key={option.value} value={option.value} className="text-sm">
              {option.label}
            </option>
          ))}
        </select>
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2">
          <label className="text-sm font-medium">Order</label>
          <FieldInfoComponent name={BRAND_EVIDENCE_FIELDS.ORDER} fieldDetails={data?.fieldInfoArray ?? []} />
        </div>
        <Input
          required
          type="number"
          name="order"
          placeholder="Usage(s) order"
          min={1}
          value={order ?? ""}
          onChange={(e) => setOrder(+e.target.value)}
        />
      </div>

      <div className="flex items-center space-x-2 border-t pt-2">
        <input
          type="checkbox"
          id="create-another"
          checked={createAnother}
          className="rounded border border-input"
          onChange={(e) => setCreateAnother(e.target.checked)}
        />
        <label htmlFor="create-another" className="text-sm leading-none font-medium">
          Create another?
        </label>
      </div>

      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> Creating...
          </span>
        ) : (
          fetcher.data?.errorMessage != null && <p className="text-sm text-destructive">{fetcher.data.errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button type="button" variant="ghost">
            Cancel
          </Button>
        </DialogClose>
        <Button type="submit">Create Theme</Button>
      </div>
    </fetcher.Form>
  )
}

function BrandEvidenceThemesDeleteAll() {
  const fetcher = useFetcher<ActionData>()
  const data = useRouteLoaderData<LoaderData>(routeId)

  if (data == null || !hasPermission("deleteAccess")) return null

  if (fetcher.data != null && fetcher.data.action !== "DELETE_ALL") return null

  return (
    <Popover key={fetcher.data?.key}>
      <PopoverTrigger asChild>
        <Button variant="destructive">
          <TrashIcon /> Delete All
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end">
        <p className="text-sm text-pretty">
          Are you sure you want to <span className="font-semibold">DELETE ALL</span> Evidence Theme in the &quot;
          <span className="font-semibold">{data.loadedOrganisation}</span>&quot; organisation?
        </p>
        <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
          <input type="hidden" name="action" value="DELETE_ALL" />
          <input type="hidden" name="project" value={data.loadedProject} />
          <input type="hidden" name="organisation" value={data.loadedOrganisation} />
          {fetcher.state !== "idle" && (
            <span className="flex items-center justify-start gap-1 pr-4">
              <LoaderCircleIcon className="size-4 animate-spin" /> Deleting
            </span>
          )}
          <div className="flex-1" />
          <PopoverClose asChild>
            <Button>Cancel</Button>
          </PopoverClose>
          <Button type="submit" variant="ghost">
            Confirm
          </Button>
        </fetcher.Form>
      </PopoverContent>
    </Popover>
  )
}

function BrandEvidenceThemeEdit(theme: EvidenceTheme) {
  const data = useRouteLoaderData<LoaderData>(routeId)
  const [isOpen, setIsOpen] = useState(false)

  if (data == null || !hasPermission("writeAccess")) return null

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Edit Evidence Theme">
          <PencilIcon />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader className="border-b border-b-muted-foreground pb-2">
          <DialogTitle>Edit Evidence Theme & Usage</DialogTitle>
        </DialogHeader>
        <BrandEvidenceThemeEditForm {...theme} onSuccess={() => setIsOpen(false)} />
      </DialogContent>
    </Dialog>
  )
}

function BrandEvidenceThemeEditForm({
  name,
  code,
  order,
  usage,
  onSuccess,
}: EvidenceTheme & { onSuccess: () => void }) {
  const fetcher = useFetcher<MainActionData>()
  const data = useRouteLoaderData<LoaderData>(routeId)

  useEffect(() => {
    if (fetcher.state !== "loading" || fetcher.data?.errorMessage != null) return
    onSuccess()
  }, [fetcher.data, fetcher.state, onSuccess])

  if (data == null) return null

  return (
    <fetcher.Form method="PATCH" className="space-y-4">
      <input type="hidden" name="action" value="EDIT" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />

      <div className="grid grid-cols-[minmax(0,1fr)_minmax(5rem,max-content)] grid-rows-[max-content_minmax(0,1fr)] gap-x-2 gap-y-1">
        <div className="row-span-full grid grid-rows-subgrid">
          <div className="mb-1 flex items-center gap-2">
            <label className="text-sm font-medium">Name</label>
            <FieldInfoComponent name={BRAND_EVIDENCE_FIELDS.NAME} fieldDetails={data?.fieldInfoArray ?? []} />
          </div>
          <input type="hidden" name="name" value={name} />
          <Input type="text" required name="newName" defaultValue={name} autoFocus />
        </div>
        <div className="row-span-full grid grid-rows-subgrid">
          <div className="mb-1 flex items-center gap-2">
            <label className="text-sm font-medium">Code</label>
            <FieldInfoComponent name={BRAND_EVIDENCE_FIELDS.CODE} fieldDetails={data?.fieldInfoArray ?? []} />
          </div>
          <input type="hidden" name="code" value={code} />
          <Input type="text" required name="newCode" defaultValue={code} className="[field-sizing:content] max-w-40" />
        </div>
        <div className="col-span-full row-span-full row-start-3 text-xs text-muted-foreground">
          Modifying the fields above would affect all other usages of this theme.
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2">
          <label className="text-sm font-medium">Usage</label>
          <FieldInfoComponent name={BRAND_EVIDENCE_FIELDS.USAGE} fieldDetails={data?.fieldInfoArray ?? []} />
        </div>
        <input type="hidden" name="usage" value={usage} />
        <Select required name="newUsage" defaultValue={usage}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {UsageOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2">
          <label className="text-sm font-medium">Order</label>
          <FieldInfoComponent name={BRAND_EVIDENCE_FIELDS.ORDER} fieldDetails={data?.fieldInfoArray ?? []} />
        </div>
        <input type="hidden" name="order" value={order} />
        <Input type="number" required name="newOrder" defaultValue={order} min={1} />
      </div>

      <div className="flex items-center justify-start gap-4">
        {fetcher.state !== "idle" ? (
          <span className="flex items-center justify-start gap-1 pr-4">
            <LoaderCircleIcon className="size-4 animate-spin" /> Loading
          </span>
        ) : (
          <p className="text-sm text-destructive empty:hidden">{fetcher.data?.errorMessage}</p>
        )}
        <div className="flex-1" />
        <DialogClose asChild>
          <Button variant="ghost">Cancel</Button>
        </DialogClose>
        <Button type="submit">Submit</Button>
      </div>
    </fetcher.Form>
  )
}

function BrandEvidenceThemeDelete(theme: EvidenceTheme) {
  if (!hasPermission("deleteAccess")) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="icon" aria-label="Delete Evidence Theme">
          <TrashIcon className="text-red-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="max-w-sm">
        <p className="text-sm text-pretty">
          &quot;<span className="font-semibold">{theme.name}</span>&quot; (<ThemeUsageLabel usage={theme.usage} />) will
          be deleted from the Evidence Themes List. Are you sure?
        </p>
        <BrandEvidenceThemeDeleteForm {...theme} />
      </PopoverContent>
    </Popover>
  )
}

function BrandEvidenceThemeDeleteForm({ name, usage }: EvidenceTheme) {
  const fetcher = useFetcher<MainActionData>()
  const data = useRouteLoaderData<LoaderData>(routeId)

  if (data == null) return null

  return (
    <fetcher.Form method="DELETE" className="flex items-center justify-start gap-3 pt-4">
      <input type="hidden" name="action" value="DELETE" />
      <input type="hidden" name="organisation" value={data.loadedOrganisation} />
      <input type="hidden" name="project" value={data.loadedProject} />
      <input type="hidden" name="name" value={name} />
      <input type="hidden" name="usage" value={usage} />
      {fetcher.state !== "idle" ? (
        <span className="flex items-center justify-start gap-1 pr-4">
          <LoaderCircleIcon className="size-4 animate-spin" /> Deleting
        </span>
      ) : (
        <p className="text-sm text-destructive empty:hidden">{fetcher.data?.errorMessage}</p>
      )}
      <div className="flex-1" />
      <PopoverClose asChild>
        <Button>Cancel</Button>
      </PopoverClose>
      <Button type="submit" variant="ghost">
        Confirm
      </Button>
    </fetcher.Form>
  )
}

BrandEvidenceThemes.loader = loader
BrandEvidenceThemes.action = action
BrandEvidenceThemes.Main = Main
BrandEvidenceThemes.routeId = routeId

Main.action = mainAction
